import { Command } from 'commander'
import ora from 'ora'
import pc from 'picocolors'
import { getCcusageData } from '../ccusage-proxy.js'
import { getApiKey, getApiEndpoint, setupApiKey } from '../config.js'

export const syncCommand = new Command('sync')
  .description('Sync Claude usage data to database')
  .option('-k, --api-key <key>', 'API key ID to sync data for')
  .option('--since <date>', 'Start date for syncing (ISO format)')
  .option('--until <date>', 'End date for syncing (ISO format)')
  .option('--dry-run', 'Show what would be synced without actually syncing')
  .option('--remote', 'Sync to remote VibeTime server (default)')
  .option('--local', 'Sync to local database only')
  .action(async (options) => {
    const spinner = ora('Initializing...').start()
    
    try {
      // Get API configuration
      let apiKey: string
      try {
        apiKey = await getApiKey({ apiKey: options.apiKey })
      } catch (error) {
        spinner.stop()
        // API key not configured - prompt to set it up
        console.log(pc.yellow('\n⚠️  API key is required for syncing data to VibeTime cloud.\n'))
        const confirm = await import('@inquirer/prompts').then(m => m.confirm({
          message: 'Would you like to set up your API key now?',
          default: true
        }))
        
        if (confirm) {
          apiKey = await setupApiKey()
        } else {
          console.log(pc.gray('\nTo configure later, run: ') + pc.green('vibetime config set'))
          process.exit(0)
        }
        spinner.start('Initializing...')
      }
      
      const apiEndpoint = getApiEndpoint()

      spinner.text = 'Loading usage data from ccusage...'
      
      // Get data from ccusage with date filtering
      const ccusageOptions: string[] = []
      if (options.since) {
        // Convert ISO date to YYYYMMDD for ccusage
        const sinceDate = new Date(options.since)
        const sinceDateStr = sinceDate.toISOString().split('T')[0].replace(/-/g, '')
        ccusageOptions.push('--since', sinceDateStr)
      }
      if (options.until) {
        // Convert ISO date to YYYYMMDD for ccusage
        const untilDate = new Date(options.until)
        const untilDateStr = untilDate.toISOString().split('T')[0].replace(/-/g, '')
        ccusageOptions.push('--until', untilDateStr)
      }
      ccusageOptions.push('--mode', 'calculate') // Always calculate costs
      ccusageOptions.push('--instances') // Get per-model breakdown

      const dailyData = await getCcusageData('daily', ccusageOptions)
      
      // Handle both 'daily' array and 'projects' object formats
      let dailyArray: any[] = []
      
      if (dailyData.daily && Array.isArray(dailyData.daily)) {
        // Direct daily array format
        dailyArray = dailyData.daily
      } else if (dailyData.projects) {
        // Projects object format - flatten all project days
        for (const projectDays of Object.values(dailyData.projects) as any[]) {
          dailyArray = dailyArray.concat(projectDays)
        }
      }
      
      if (dailyArray.length === 0) {
        spinner.succeed('No usage data found for the specified period')
        return
      }

      spinner.text = `Processing ${dailyArray.length} days of usage data...`

      // Transform ccusage data to vibetime-web format
      const dailyAggregates = dailyArray.map((day: any) => {
        // Group by model if modelBreakdowns exist
        if (day.modelBreakdowns && day.modelBreakdowns.length > 0) {
          return day.modelBreakdowns.map((mb: any) => ({
            date: day.date,
            model: mb.modelName || mb.model || 'unknown',
            totalInputTokens: mb.inputTokens || 0,
            totalOutputTokens: mb.outputTokens || 0,
            totalCacheCreationTokens: mb.cacheCreationTokens || 0,
            totalCacheReadTokens: mb.cacheReadTokens || 0,
            totalCostUSD: mb.cost || 0
          }))
        } else {
          // No model breakdown, send as aggregate
          return [{
            date: day.date,
            model: day.modelsUsed?.[0] || 'unknown',
            totalInputTokens: day.inputTokens || 0,
            totalOutputTokens: day.outputTokens || 0,
            totalCacheCreationTokens: day.cacheCreationTokens || 0,
            totalCacheReadTokens: day.cacheReadTokens || 0,
            totalCostUSD: day.totalCost || 0
          }]
        }
      }).flat()

      spinner.text = `Syncing ${dailyAggregates.length} aggregates to VibeTime API...`

      if (options.dryRun) {
        spinner.succeed('Dry run - showing what would be synced:')
        console.log(pc.cyan('\nDaily aggregates to sync:'))
        
        // Group by date for display
        const byDate = dailyAggregates.reduce((acc: any, agg: any) => {
          if (!acc[agg.date]) acc[agg.date] = []
          acc[agg.date].push(agg)
          return acc
        }, {})
        
        Object.entries(byDate).forEach(([date, aggs]: [string, any]) => {
          const totalCost = aggs.reduce((sum: number, a: any) => sum + a.totalCostUSD, 0)
          const totalTokens = aggs.reduce((sum: number, a: any) => 
            sum + a.totalInputTokens + a.totalOutputTokens + a.totalCacheCreationTokens + a.totalCacheReadTokens, 0)
          console.log(`  ${date}: ${totalTokens.toLocaleString()} tokens, $${totalCost.toFixed(2)} (${aggs.length} models)`)
        })
        return
      }

      // Sync to VibeTime API using the daily aggregate endpoint
      const response = await fetch(`${apiEndpoint}/api/usage/daily`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          dailyAggregates
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API request failed: ${response.status} ${response.statusText}\n${errorText}`)
      }

      const result = await response.json() as { success: boolean; count: number; message: string }
      spinner.succeed(`✅ ${result.message || `Successfully synced ${result.count} aggregates`}`)
      
      // Show summary
      console.log(pc.green('\nSync Summary:'))
      const totalDays = new Set(dailyAggregates.map((a: any) => a.date)).size
      const totalModels = new Set(dailyAggregates.map((a: any) => a.model)).size
      console.log(`- Days synced: ${totalDays}`)
      console.log(`- Models tracked: ${totalModels}`)
      console.log(`- Total aggregates: ${result.count}`)
      
      // Show dashboard URL
      console.log(pc.cyan('\n📊 View your usage dashboard:'))
      console.log(pc.blue(`${apiEndpoint}/dashboard/usage`))
    } catch (error: unknown) {
      spinner.fail('Sync failed')
      if (error instanceof Error) {
        console.error(pc.red('Error:'), error.message)
      } else {
        console.error(pc.red('Error:'), error)
      }
      process.exit(1)
    }
  })